import { Flex, Popup, Text, toast } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { memo, useCallback } from 'react';
import { useCopyToClipboard } from 'react-use';
import { t } from 'services/localization';
import { useGetWalletFundingAccounts } from 'shared/data/wallets/v1';
import { useCompanyIdBasedOnUrl } from 'shared/hooks/useCompanyIdBasedOnUrl';

import { fundWalletPopupState } from '../../FundWalletPopup.states';
import { FundWalletCard } from '../FundWalletCard/FundWalletCard';
import { FundWalletCardField } from '../FundWalletCardField/FundWalletCardField';

export const FundWalletPopupContent = memo(() => {
    const { companyId: companyIdFromUrl } = useCompanyIdBasedOnUrl();

    const [, setCopiedText] = useCopyToClipboard();
    const popupState = useAtomValue(fundWalletPopupState);

    const { data: walletFundingAccounts, isLoading: isLoadingGetWalletFundingAccounts } = useGetWalletFundingAccounts(
        {
            path: {
                organizationId: popupState.companyId ?? companyIdFromUrl,
                walletId: popupState.walletId,
            },
        },
        {
            staleTime: Infinity,
        }
    );

    const createCopyAllFieldsHandler = useCallback(
        (title: string, fields: { field: string; value: string }[]) => () => {
            const allFieldsText = fields.map((item) => `${item.field}: ${item.value}`).join('\n');

            setCopiedText(`${title}\n${allFieldsText}`);
            toast.success(t('copied_to_clipboard'));
        },
        [setCopiedText]
    );

    const createCopyFieldHandler = useCallback(
        (value: string) => () => {
            setCopiedText(value);
            toast.success(t('copied_to_clipboard'));
        },
        [setCopiedText]
    );

    return (
        <>
            <Popup.Header
                title={t('currencycode_account_details', {
                    currencyCode: (popupState.currencyCode || '').toUpperCase(),
                })}
            />

            <Popup.Body spacing='32'>
                <Flex direction='column'>
                    <Text font='label' color='midnight70'>
                        {t('use_the_country_of_orig_or_funding_your_account')}
                    </Text>

                    {isLoadingGetWalletFundingAccounts ? (
                        <FundWalletCard.Loading />
                    ) : (
                        walletFundingAccounts?.items.map((item) => (
                            <FundWalletCard
                                key={item.title}
                                title={item.title}
                                description={item.description}
                                onCopy={createCopyAllFieldsHandler(item.title, item.properties)}
                            >
                                {item.properties.map((property) => (
                                    <FundWalletCardField
                                        {...property}
                                        key={property.field}
                                        onCopy={createCopyFieldHandler(property.value)}
                                    />
                                ))}
                            </FundWalletCard>
                        ))
                    )}
                </Flex>
            </Popup.Body>
        </>
    );
});

FundWalletPopupContent.displayName = 'FundWalletPopupContent';
